// ── App State Machine ────────────────────────────────────────────────────
// Contains setState() and initWatchface(), extracted from index.html (Task 28).

import { state, filters } from '../state';
import type { AppState } from '../types';
import { dom } from '../dom';
import {
  MINI_CLOCK_SCALE,
  LOCK_WORLD_ZOOM,
  MENU_SCALE_FINAL,
  PERSISTENT_CLOCK_ENABLED,
} from '../config';
import {
  getItemSettledScale,
  applyItemTransform,
  isInstallerFlowActive,
} from '../layout/ringLayout';
import { abortInstallerFlow, resetInstallerScannerVisual } from '../lock/unlockTransition';
import {
  drawLines,
  drawLockLinesOverlay,
  applyLockItemReveal,
  clearLinesCanvas,
} from '../render/lineRenderer';
import { setHighlight } from '../items/highlight';
import { scrambleToText } from '../render/matrixScramble';
import { WatchfaceHost } from '../watchface/host';
import { resetHomeUIState } from '../home/homeOptions';
import { invalidateCommit } from '../layout/commit';
// AnalogShaderFace is intentionally not registered — currently only the
// physics-balls face is in rotation. Importing it would pull ~1300 lines of
// shader code into the bundle for no runtime use, so the import is commented
// out. Re-enable here + in the register() list below to bring it back.
// import { AnalogShaderFace } from '../watchface/analogShader';
import { PhysicsBallsFace } from '../watchface/physicsBalls';

/** Convenience accessor — asserts watchfaceHost is non-null for internal call sites. */
function wfh() { return state.watchfaceHost!; }

// ── Watchface Init ───────────────────────────────────────────────────────
export function initWatchface(): void {
  state.watchfaceHost = new WatchfaceHost(dom.lockScreen, { keepAliveFaceIndices: [] });
  // Only physics-balls is registered. WatchfaceHost.triggerSwap() detects
  // registry.length === 1 and plays the chrono-out → chrono-in animation
  // without switching faces, which is exactly what we want for the B / 't'
  // button press.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  wfh().register(PhysicsBallsFace as any);
  wfh().mountFace(wfh().activeIndex);
  wfh().startClockLoop();
}

// ── State Machine ────────────────────────────────────────────────────────
export function setState(newState: AppState): void {
  const prev = state.appState;
  state.appState = newState;
  console.log(`[Loop OS] State: ${prev} → ${newState}`);
  if (newState !== 'home' && isInstallerFlowActive()) {
    abortInstallerFlow({ keepReticleText: false });
  }

  // Commit cache invalidation: this function writes to item styles directly
  // (item.el.style.opacity, etc.) as part of the state transition. Clear the
  // per-element cache so the next L4 commit pass in the rAF loop doesn't
  // skip a write based on a stale cached value.
  for (const item of state.items) invalidateCommit(item.el);
  invalidateCommit(dom.homeOptionsOverlay);
  invalidateCommit(dom.homeOptionRemove);
  invalidateCommit(dom.homeOptionEdit);
  invalidateCommit(dom.homeOptionsConnectorPath);
  invalidateCommit(dom.lines);

  if (newState === 'lock') {
    resetHomeUIState();
    state.installerScannerLineTint = 0;
    dom.lockScreen.className = '';
    // Don't clear the inline transform/opacity here — mainLoop's lock
    // phase rewrites them every frame (including this one). Clearing them
    // would briefly demote the GPU compositor layer (no translate3d → no
    // 3D context), then mainLoop re-promotes it next frame, producing a
    // one-frame paint glitch on the watchface canvas — visible as the
    // clock hands "flickering" once on every state transition.
    dom.relockCapture.style.transform = '';
    state.persistentClockScale = MINI_CLOCK_SCALE;
    if (dom.lockLinesOverlay) {
      dom.lockLinesOverlay.style.display = 'block';
      dom.lockLinesOverlay.style.opacity = '1';
      dom.lockLinesOverlay.style.zIndex = '';
    }
    state.lockOverlayPendingHide = false;
    dom.reticle.classList.remove('visible');
    dom.menuContainer.style.pointerEvents = 'none';
    document.querySelectorAll('.ring-guide').forEach(g => g.classList.remove('visible'));
    // Items stay at final position + settled scale in lock state; the
     // .item CSS rule hides them with opacity: 0 and transitions opacity
     // back to 1 when unlocking. Keeping the transform stable avoids the
     // scale-0-to-settled jump ("pop") that low-end devices render
     // as a single chunky frame.
    for (const item of state.items) {
      item.currentX = item.baseX;
      item.currentY = item.baseY;
      item.flowX = item.baseX;
      item.flowY = item.baseY;
      item.targetX = item.baseX as number;
      item.targetY = item.baseY as number;
      item.currentScale = getItemSettledScale(item);
      item.pressed = false;
      item.pressScaleMul = 1;
      item.visualOpacity = 0;
      item.visualTint = 0;
      item.el.style.opacity = '0';
      if (item.inner) item.inner.style.filter = '';
      if (item.scannerLayerEl) resetInstallerScannerVisual(item);
      else applyItemTransform(item);
    }
    // Draw lock lines immediately on state switch so there is no one-frame gap.
    drawLockLinesOverlay(0);
    state.inputActive = false;
    state.worldZoom = LOCK_WORLD_ZOOM;
    state.menuScale = MENU_SCALE_FINAL;
    if (state.watchfaceHost) {
      wfh().startClockLoop();
      wfh().resume(0);
      wfh().setZoomProgress(1);
      wfh().setMinimalMode(0);
    }
    if (PERSISTENT_CLOCK_ENABLED) dom.relockCapture.classList.add('hidden');
  } else if (newState === 'unlocking') {
    resetHomeUIState();
    dom.lockScreen.classList.add('state-unlocking');
    dom.menuContainer.style.pointerEvents = 'none';
    document.querySelectorAll('.ring-guide').forEach(g => g.classList.add('visible'));
    // Pre-paint items to final opacity synchronously so the CSS
    // `transition: opacity 320ms` on .item starts fading the entire ring
    // cluster the instant the user presses unlock — no wait for the first
    // rAF tick. On a slow device that first rAF could be ~33 ms away;
    // without this sync paint the user would see a brief stall before
    // items start appearing.
    for (const item of state.items) {
      item.currentScale = getItemSettledScale(item);
      item.visualOpacity = 1;
      item.el.style.opacity = '1';
      if (item.inner) item.inner.style.filter = '';
      if (item.scannerLayerEl) resetInstallerScannerVisual(item);
      else applyItemTransform(item);
    }
  } else if (newState === 'home') {
    resetHomeUIState();
    state.installerScannerLineTint = 0;
    // Snap to final geometry before switching renderers (lock overlay -> home lines)
    // so line length does not jump on the last frame.
    applyLockItemReveal(1);
    for (const item of state.items) {
      item.currentX = item.baseX;
      item.currentY = item.baseY;
      item.flowX = item.baseX;
      item.flowY = item.baseY;
      item.targetX = item.baseX as number;
      item.targetY = item.baseY as number;
      item.currentScale = getItemSettledScale(item);
      item.pressed = false;
      item.pressScaleMul = 1;
      item.visualOpacity = 1;
      item.visualTint = 0;
      if (item.inner) item.inner.style.filter = '';
      if (item.scannerLayerEl) resetInstallerScannerVisual(item);
      else applyItemTransform(item);
      item.el.style.opacity = '1';
    }
    drawLines();
    state.worldZoom = 1;
    state.menuScale = MENU_SCALE_FINAL;

    if (PERSISTENT_CLOCK_ENABLED) {
      dom.lockScreen.classList.add('state-mini');
      dom.lockScreen.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0) scale(${state.persistentClockScale})`;
      dom.lockScreen.style.opacity = '1';
      state.lockOverlayPendingHide = false;
      if (dom.lockLinesOverlay) dom.lockLinesOverlay.style.opacity = '0';
      if (state.watchfaceHost) {
        wfh().startClockLoop();
        wfh().resume(0);
        wfh().setMinimalMode(1);
        wfh().setZoomProgress(0);
        wfh().setParallax(0, 0, 'gyro');
        wfh().setLockLineColliders([], { enabled: false });
      }
      dom.relockCapture.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0)`;
      dom.relockCapture.classList.remove('hidden');
    } else {
      dom.lockScreen.classList.add('state-hidden');
      state.lockOverlayPendingHide = true;
      if (state.watchfaceHost) {
        wfh().stopClockLoop();
        wfh().pause('home');
        wfh().setZoomProgress(0);
        wfh().setLockLineColliders([], { enabled: false });
      }
    }
    dom.reticle.classList.add('visible');
    dom.menuContainer.style.pointerEvents = 'auto';
    document.querySelectorAll('.ring-guide').forEach(g => g.classList.add('visible'));
    for (const item of state.items) {
      item.visualOpacity = 1;
      item.visualTint = 0;
      item.el.style.opacity = '1';
      if (item.inner) item.inner.style.filter = '';
    }
    state.homeRefQuat = null;
    filters.cameraX.reset(); filters.cameraY.reset();
    state.cameraNormX = 0; state.cameraNormY = 0;
    state.cameraTargetX = 0; state.cameraTargetY = 0;
  } else if (newState === 'locking') {
    resetHomeUIState();
    dom.lockScreen.classList.remove('state-mini');
    dom.lockScreen.classList.add('state-unlocking');
    dom.menuContainer.style.pointerEvents = 'none';
    dom.reticle.classList.remove('visible');
    dom.relockCapture.style.transform = '';
    dom.relockCapture.classList.add('hidden');
    if (state.highlighted) { setHighlight(state.highlighted, false); state.highlighted = null; }
    scrambleToText('•');
    state.inputActive = false;
    if (state.watchfaceHost) {
      wfh().startClockLoop();
      wfh().resume(0);
    }
    // Clear home lines canvas so only lock-lines overlay renders
    clearLinesCanvas();
    // Show lock-lines overlay ABOVE state.items (z-index 76 > #world 75) during locking
    if (dom.lockLinesOverlay) {
      dom.lockLinesOverlay.style.display = 'block';
      dom.lockLinesOverlay.style.opacity = '1';
      dom.lockLinesOverlay.style.zIndex = '76';
    }
    drawLockLinesOverlay(1);
  }
}
